/**
 * Products API
 *
 * API functions for product operations.
 * Types are derived from the generated OpenAPI contract.
 */

import { apiClient } from './client';
import type {
  Product,
  ProductListResponse,
  FilterOptions,
  MassEditExecuteRequest,
  MassEditExecuteResponse,
  MassEditPreviewRequest,
  MassEditPreviewResponse,
  BulkFieldImportExecuteRequest,
  BulkFieldImportExecuteResponse,
  BulkFieldImportPreviewResponse,
  BulkFieldImportRow,
  ProductHistoryResponse,
  ProductUserInfo,
  RestoreVersionRequest,
  PendingEditsResponse,
  GroupedSubmissionListResponse,
  BulkReviewRequest,
  BulkReviewResponse,
  FuzzyMatchResult,
  ProductStats,
  SearchResult,
  FieldCounts,
  FieldSearchResult,
  BrandSearchResult,
  BrandProfile,
} from './generated/contract-types';
import type { ProductListParams, ProductFilters } from './types';

export type { FieldCounts, SearchResult, FieldSearchResult, BrandSearchResult, BrandProfile };
export type {
  BulkFieldImportExecuteRequest,
  BulkFieldImportExecuteResponse,
  BulkFieldImportPreviewResponse,
  BulkFieldImportRow,
};
export type ProductImageView = 'front' | 'side' | 'top';

export interface ProductActivityRollupItem {
  changedByUserId: number | null;
  actorName: string | null;
  actorEmail: string | null;
  addedCount: number;
  modifiedCount: number;
  deactivatedCount: number;
  totalEvents: number;
  distinctProducts: number;
  lastActivityAt: string | null;
}

export interface ProductActivityRollupResponse {
  days: number;
  dateFrom: string;
  dateTo: string;
  totals: {
    addedCount: number;
    modifiedCount: number;
    deactivatedCount: number;
    totalEvents: number;
    distinctProducts: number;
  };
  items: ProductActivityRollupItem[];
}

export type ProductImageAvailability = {
  view: ProductImageView;
  available: boolean;
  source: 'submission' | 'product' | 'missing';
};

export type SppExecutableMetadata = {
  id: number;
  version: string;
  filename: string;
  contentType: string;
  sizeBytes: number;
  sha256: string;
  uploadedByUserId: number | null;
  createdAt: string;
};

export type ProductImageViewsResponse = {
  views: ProductImageView[];
  images: ProductImageAvailability[];
  degraded: boolean;
};

type ParamInput = Record<string, unknown> | ProductListParams | ProductFilters;

const buildParams = (params: ParamInput): Record<string, string | number | boolean> => {
  const normalized: Record<string, string | number | boolean> = {};

  Object.entries(params as Record<string, unknown>).forEach(([key, value]) => {
    if (value === undefined || value === null || value === '') {
      return;
    }

    if (Array.isArray(value)) {
      const filteredValues = value.map((entry) => String(entry).trim()).filter(Boolean);
      if (filteredValues.length > 0) {
        normalized[key] = filteredValues.join(',');
      }
      return;
    }

    normalized[key] = value as string | number | boolean;
  });

  return normalized;
};

export const getProducts = async (params: ProductListParams = {}): Promise<ProductListResponse> => {
  const response = await apiClient.get<ProductListResponse>('/products/', {
    params: buildParams(params),
  });
  return response.data;
};

export const exportProductsExcel = async (
  params: ProductListParams = {}
): Promise<{ data: Blob; contentDisposition?: string }> => {
  const response = await apiClient.get<Blob>('/products/export/excel', {
    params: buildParams(params),
    responseType: 'blob',
  });
  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  return { data: response.data, contentDisposition };
};

export const downloadLatestSppExecutable = async (): Promise<{
  data: Blob;
  contentDisposition?: string;
}> => {
  const response = await apiClient.get<Blob>('/products/spp-executable/latest', {
    responseType: 'blob',
  });
  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  return { data: response.data, contentDisposition };
};

export const getLatestSppExecutableMetadata = async (): Promise<SppExecutableMetadata> => {
  const response = await apiClient.get<SppExecutableMetadata>(
    '/products/spp-executable/latest/metadata'
  );
  return response.data;
};

export const uploadSppExecutable = async (
  file: File,
  version?: string
): Promise<SppExecutableMetadata> => {
  const formData = new FormData();
  formData.append('file', file);
  if (version) {
    formData.append('version', version);
  }
  const response = await apiClient.post<SppExecutableMetadata>(
    '/products/spp-executable',
    formData
  );
  return response.data;
};

export const getProduct = async (id: number): Promise<Product> => {
  const response = await apiClient.get<Product>(`/products/${id}`);
  return response.data;
};

export const getProductImageViews = async (
  productId: number
): Promise<ProductImageViewsResponse> => {
  const response = await apiClient.get<ProductImageViewsResponse>(
    `/images/products/${productId}/views`
  );
  return response.data;
};

export const downloadProductImage = async (
  productId: number,
  view: ProductImageView
): Promise<{ data: Blob; contentDisposition?: string }> => {
  const response = await apiClient.get<Blob>(`/images/products/${productId}/download`, {
    params: { view },
    responseType: 'blob',
  });
  const contentDisposition = response.headers['content-disposition'] as string | undefined;
  return { data: response.data, contentDisposition };
};

export const getFilterOptions = async (filters: ProductFilters = {}): Promise<FilterOptions> => {
  const params = buildParams(filters);
  const response = await apiClient.get<FilterOptions>('/products/filter-options', {
    params,
  });
  return response.data;
};

export const getFieldCounts = async (): Promise<FieldCounts> => {
  const response = await apiClient.get<FieldCounts>('/products/field-counts');
  return response.data;
};

export const createProduct = async (data: Partial<Product>): Promise<Product> => {
  const response = await apiClient.post<Product>('/products/', data);
  return response.data;
};

export const updateProduct = async (id: number, data: Partial<Product>): Promise<Product> => {
  const response = await apiClient.patch<Product>(`/products/${id}`, data);
  return response.data;
};

export const deactivateProduct = async (id: number): Promise<Product> => {
  const response = await apiClient.post<Product>(`/products/${id}/deactivate`);
  return response.data;
};

export const reactivateProduct = async (id: number): Promise<Product> => {
  const response = await apiClient.post<Product>(`/products/${id}/reactivate`);
  return response.data;
};

export const previewMassEdit = async (
  request: MassEditPreviewRequest
): Promise<MassEditPreviewResponse> => {
  const response = await apiClient.post<MassEditPreviewResponse>(
    '/products/mass-edit/preview',
    request
  );
  return response.data;
};

export const executeMassEdit = async (
  request: MassEditExecuteRequest
): Promise<MassEditExecuteResponse> => {
  const response = await apiClient.post<MassEditExecuteResponse>(
    '/products/mass-edit/execute',
    request
  );
  return response.data;
};

export const previewBulkFieldImport = async (
  file: File
): Promise<BulkFieldImportPreviewResponse> => {
  const formData = new FormData();
  formData.append('file', file);
  const response = await apiClient.post<BulkFieldImportPreviewResponse>(
    '/products/mass-edit/import/preview',
    formData
  );
  return response.data;
};

export const executeBulkFieldImport = async (
  request: BulkFieldImportExecuteRequest
): Promise<BulkFieldImportExecuteResponse> => {
  const response = await apiClient.post<BulkFieldImportExecuteResponse>(
    '/products/mass-edit/import/execute',
    request
  );
  return response.data;
};

export const deleteProduct = async (id: number): Promise<void> => {
  await apiClient.delete(`/products/${id}`);
};

export const getProductStats = async (): Promise<ProductStats> => {
  const response = await apiClient.get<ProductStats>('/products/stats');
  return response.data;
};

export const getProductActivityRollups = async (
  days: number = 30
): Promise<ProductActivityRollupResponse> => {
  const response = await apiClient.get<ProductActivityRollupResponse>(
    '/products/activity/rollups',
    {
      params: { days },
    }
  );
  return response.data;
};

export const searchProducts = async (
  query: string,
  limit: number = 10
): Promise<SearchResult[]> => {
  if (!query || query.length < 2) {
    return [];
  }
  const response = await apiClient.get<SearchResult[]>('/products/search', {
    params: { q: query, limit },
  });
  return response.data;
};

export const getProductHistory = async (
  productId: number,
  limit: number = 50,
  offset: number = 0
): Promise<ProductHistoryResponse> => {
  const response = await apiClient.get<ProductHistoryResponse>(`/products/${productId}/history`, {
    params: { limit, offset },
  });
  return response.data;
};

export const getProductUserInfo = async (productId: number): Promise<ProductUserInfo> => {
  const response = await apiClient.get<ProductUserInfo>(`/products/${productId}/user-info`);
  return response.data;
};

export const restoreProductVersion = async (
  productId: number,
  request: RestoreVersionRequest
): Promise<Product> => {
  const response = await apiClient.post<Product>(`/products/${productId}/history/restore`, request);
  return response.data;
};

export const getPendingEdits = async (productId: number): Promise<PendingEditsResponse> => {
  const response = await apiClient.get<PendingEditsResponse>(
    `/products/${productId}/pending-edits`
  );
  return response.data;
};

export const getGroupedSubmissions = async (
  status?: string,
  page: number = 1,
  perPage: number = 50,
  source?: 'user' | 'iri'
): Promise<GroupedSubmissionListResponse> => {
  const params = new URLSearchParams();
  if (status) params.append('status', status);
  if (source) params.append('source', source);
  params.append('page', page.toString());
  params.append('perPage', perPage.toString());

  const response = await apiClient.get<GroupedSubmissionListResponse>(
    `/products/submissions/grouped?${params.toString()}`
  );
  return response.data;
};

export const bulkReviewSubmissions = async (
  request: BulkReviewRequest
): Promise<BulkReviewResponse> => {
  const response = await apiClient.post<BulkReviewResponse>(
    '/products/submissions/bulk-review',
    request
  );
  return response.data;
};

export const fuzzyMatchBrand = async (
  brand: string,
  limit: number = 5
): Promise<FuzzyMatchResult> => {
  if (!brand || brand.trim().length < 2) {
    return { query: '', exactMatch: false, matches: [] };
  }
  const response = await apiClient.get<FuzzyMatchResult>('/products/fuzzy-match', {
    params: { brand: brand.trim(), limit },
  });
  return response.data;
};

export const searchBrands = async (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
): Promise<BrandSearchResult[]> => {
  if (!query || query.length < 1) {
    return [];
  }
  const response = await apiClient.get<BrandSearchResult[]>('/products/brands/search', {
    params: buildParams({ q: query, limit, ...filters }),
  });
  return response.data;
};

export const getBrandProfile = async (brand: string): Promise<BrandProfile | null> => {
  if (!brand || brand.trim().length < 1) {
    return null;
  }
  const response = await apiClient.get<BrandProfile>('/products/brands/profile', {
    params: { brand: brand.trim() },
  });
  return response.data;
};

export const searchManufacturers = async (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
): Promise<FieldSearchResult[]> => {
  if (!query || query.length < 1) {
    return [];
  }
  const response = await apiClient.get<FieldSearchResult[]>('/products/manufacturers/search', {
    params: buildParams({ q: query, limit, ...filters }),
  });
  return response.data;
};

export const searchCategories = async (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
): Promise<FieldSearchResult[]> => {
  if (!query || query.length < 1) {
    return [];
  }
  const response = await apiClient.get<FieldSearchResult[]>('/products/categories/search', {
    params: buildParams({ q: query, limit, ...filters }),
  });
  return response.data;
};

export const searchStyles = async (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
): Promise<FieldSearchResult[]> => {
  if (!query || query.length < 1) {
    return [];
  }
  const response = await apiClient.get<FieldSearchResult[]>('/products/styles/search', {
    params: buildParams({ q: query, limit, ...filters }),
  });
  return response.data;
};

export const searchPackages = async (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
): Promise<FieldSearchResult[]> => {
  if (!query || query.length < 1) {
    return [];
  }
  const response = await apiClient.get<FieldSearchResult[]>('/products/packages/search', {
    params: buildParams({ q: query, limit, ...filters }),
  });
  return response.data;
};

export const searchSizes = async (
  query: string,
  limit: number = 20,
  filters: ProductFilters = {}
): Promise<FieldSearchResult[]> => {
  if (!query || query.length < 1) {
    return [];
  }
  const response = await apiClient.get<FieldSearchResult[]>('/products/sizes/search', {
    params: buildParams({ q: query, limit, ...filters }),
  });
  return response.data;
};

export interface PackageSizeSuggestion {
  packageCode: string;
  size: string;
  packCount: number | null;
  containerType: string | null;
  method: 'package_pattern';
}

export interface DimensionSuggestion {
  packageCode: string;
  width: number;
  height: number;
  depth: number;
  unit: 'imperial';
  sampleSize: number;
  method: 'mode' | 'median' | 'mixed' | 'average';
  sources: {
    width: 'mode' | 'median' | 'average';
    height: 'mode' | 'median' | 'average';
    depth: 'mode' | 'median' | 'average';
  };
}

export const getPackageSizeSuggestion = async (
  packageValue: string
): Promise<PackageSizeSuggestion | null> => {
  if (!packageValue || packageValue.trim().length < 1) {
    return null;
  }
  const response = await apiClient.get<PackageSizeSuggestion | null>(
    '/products/packages/size-suggestion',
    { params: { package: packageValue.trim() } }
  );
  return response.data;
};

export const getDimensionSuggestions = async (
  productId: number,
  packageCode: string
): Promise<DimensionSuggestion | null> => {
  if (!packageCode || packageCode.trim().length < 3) {
    return null;
  }
  const response = await apiClient.get<DimensionSuggestion | null>(
    `/products/${productId}/dimensions/suggestions`,
    { params: { packageCode: packageCode.trim() } }
  );
  return response.data;
};

export const getDimensionSuggestionsByPackage = async (
  packageCode: string
): Promise<DimensionSuggestion | null> => {
  if (!packageCode || packageCode.trim().length < 3) {
    return null;
  }
  const response = await apiClient.get<DimensionSuggestion | null>(
    '/products/dimensions/suggestions',
    { params: { packageCode: packageCode.trim() } }
  );
  return response.data;
};

export interface PackageAverageDimensions {
  package: string;
  height: number | null;
  width: number | null;
  depth: number | null;
  sampleCount: number;
}

export const getPackageAverageDimensions = async (
  packageValue: string
): Promise<PackageAverageDimensions | null> => {
  if (!packageValue || packageValue.trim().length < 1) {
    return null;
  }
  const response = await apiClient.get<PackageAverageDimensions | null>(
    '/products/package-dimensions',
    { params: { package: packageValue.trim() } }
  );
  return response.data;
};
